import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { UploadProgressService } from '../../../core/services/upload-progress.service';

/**
 * Fixed, always-visible widget for the active concurrent file-upload batch (see
 * `batch-upload.util.ts` / `UploadProgressService`). Mounted once at the app root so it stays
 * on screen regardless of which page/modal kicked off the upload or where the user scrolls to —
 * mirrors the `app-toast` / `app-loader` pattern already used for global feedback.
 */
@Component({
  selector: 'app-upload-progress-toast',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    .upload-toast-container {
      position: fixed;
      bottom: 24px;
      left: 24px;
      z-index: 9999;
      pointer-events: none;
    }

    .upload-toast {
      pointer-events: all;
      min-width: 300px;
      max-width: 380px;
      background: #1a1d22;
      color: #f0f2f5;
      border-radius: 10px;
      padding: 13px 14px;
      box-shadow: 0 8px 32px rgba(0,0,0,0.28), 0 2px 8px rgba(0,0,0,0.18);
      animation: upload-toast-slide-in 0.22s cubic-bezier(0.2, 0.8, 0.3, 1) forwards;
    }

    @keyframes upload-toast-slide-in {
      from { transform: translateY(12px); opacity: 0; }
      to   { transform: translateY(0);    opacity: 1; }
    }

    .upload-toast-row {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 9px;
    }

    .upload-toast-spinner {
      width: 15px;
      height: 15px;
      flex-shrink: 0;
      border-radius: 50%;
      border: 2px solid rgba(240,242,245,0.2);
      border-top-color: #0370bb;
      animation: upload-toast-spin 0.8s linear infinite;
    }
    @keyframes upload-toast-spin { to { transform: rotate(360deg); } }

    .upload-toast-text {
      flex: 1;
      min-width: 0;
    }

    .upload-toast-title {
      font-size: 12.5px;
      font-weight: 700;
      color: #f0f2f5;
    }

    .upload-toast-subtitle {
      font-size: 11.5px;
      color: rgba(240,242,245,0.6);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .upload-toast-percent {
      flex-shrink: 0;
      font-size: 12.5px;
      font-weight: 700;
      color: #0370bb;
      font-variant-numeric: tabular-nums;
    }

    .upload-toast-bar {
      height: 4px;
      border-radius: 2px;
      background: rgba(240,242,245,0.14);
      overflow: hidden;
    }

    .upload-toast-bar-fill {
      height: 100%;
      border-radius: 2px;
      background: #0370bb;
      transition: width 0.15s ease-out;
    }
  `],
  template: `
    @if (progressService.progress(); as p) {
      <div class="upload-toast-container">
        <div class="upload-toast" role="status" aria-live="polite">
          <div class="upload-toast-row">
            <div class="upload-toast-spinner"></div>
            <div class="upload-toast-text">
              <div class="upload-toast-title">Uploading {{ p.total }} file{{ p.total === 1 ? '' : 's' }}</div>
              <div class="upload-toast-subtitle">{{ p.completed }} of {{ p.total }} done</div>
            </div>
            <div class="upload-toast-percent">{{ p.percent }}%</div>
          </div>
          <div class="upload-toast-bar">
            <div class="upload-toast-bar-fill" [style.width.%]="p.percent"></div>
          </div>
        </div>
      </div>
    }
  `,
})
export class UploadProgressToastComponent {
  readonly progressService = inject(UploadProgressService);
}
